<script lang="ts" module>
  import { uniqueContext } from "#lib/internal/helpers"
  import type { BaseProps } from "#lib/internal/types"

  export type Option = {
    label: string
    value: unknown
    id: string
  }

  type Props<TValue = unknown> = Omit<BaseProps<"div">, "onchange"> & {
    value?: TValue
    open?: boolean
    name?: string
    disabled?: boolean
    required?: boolean
    id?: string
    placeholder?: string
    error?: boolean | undefined
    displayDisabledIcon?: boolean
    multiselect?: boolean
    keepOpen?: boolean
    size?: VariantProps<typeof selectTriggerVariants>["size"]
    onchange?: (detail: { value: TValue }) => void
    onkeydown?: (event: KeyboardEvent) => boolean | void
    onfocusin?: (event: FocusEvent) => void
    onfocusout?: (event: FocusEvent) => void
    use?: ActionArray
    children?: Snippet
    trigger?: Snippet
    panel?: Snippet
  }

  export type SelectState = {
    // refs
    rootRef: HTMLElement | undefined
    triggerRef: HTMLElement | undefined
    panelRef: HTMLElement | undefined
    inputRef: HTMLInputElement | undefined

    // state
    open: boolean
    displayDisabledIcon: boolean
    value: unknown
    selectedIndex: number | null
    highlightedIndex: number | null
    keyboardHighlightedIndex: number | null
    options: Option[]
    idPrefix: string
    selected: Option | Option[] | null
    placeholder: string
    disabled: boolean
    size: VariantProps<typeof selectTriggerVariants>["size"]
    multiselect: boolean

    // mutators
    openSelectPanel(): void
    closeSelectPanel(): void
    toggleDialog(): void
    handleKeyDown(event: KeyboardEvent): void
    registerOption(option: Option): void
    unRegisterOption(option: Option): void
    select(index: number | null): void
    highlight(index: number, keyboard?: boolean): void
  }

  const { getContext, setContext } = uniqueContext<SelectState>()
  export const getSelectRootContext = getContext
</script>

<script lang="ts" generics="TValue = unknown">
  import { Popper } from "#lib/components/ui/popper/index"
  import {
    Keys,
    generateId,
    isScrollKey,
    useActions,
    type ActionArray,
  } from "#lib/internal/helpers"
  import { cn } from "#lib/utils"
  import type { VariantProps } from "class-variance-authority"
  import { onMount, tick, untrack, type Snippet } from "svelte"
  import Panel from "./SelectPanel.svelte"
  import SelectPortal from "./SelectPortal.svelte"
  import Trigger from "./SelectTrigger.svelte"
  import { selectFieldVariants, selectTriggerVariants } from "./variants"

  let {
    value = $bindable(),
    name = "",
    id = generateId(),
    open = $bindable(false),
    disabled = false,
    required = false,
    placeholder = "",
    error = false,
    displayDisabledIcon = false,
    size = "default",
    multiselect = false,
    keepOpen = false,
    onchange,
    onkeydown,
    onfocusin,
    onfocusout,
    use = [],
    children,
    trigger,
    panel,
    class: className,
    ...restProps
  }: Props<TValue> = $props()

  let rootRef: HTMLElement | undefined = $state(undefined)
  let triggerRef: HTMLElement | undefined = $state(undefined)
  let panelRef: HTMLElement | undefined = $state(undefined)
  let inputRef: HTMLInputElement | undefined = $state(undefined)

  // $state.raw prevents Svelte from deep-proxying option values, which is
  // critical for boulangerie/Java objects that break when wrapped in a Proxy.
  let options: Option[] = $state.raw([])
  let selected: Option | Option[] | null = $state(null)
  let selectedIndex: number | null = $state(null)
  let highlightedIndex: number | null = $state(null)
  let keyboardHighlightedIndex: number | null = $state(null)

  // Compares two values that may be primitives, plain objects, or opaque Java
  // objects from boulangerie. Primitives use ===. Objects fall through to
  // JSON.stringify. The try/catch handles circular references (e.g. TwAccount
  // objects) where JSON.stringify would throw — in that case, fall back to
  // reference equality which works because options uses $state.raw (no proxying).
  function valuesEqual(a: unknown, b: unknown): boolean {
    if (a == null || b == null) return a === b
    if (typeof a !== "object" && typeof b !== "object") return a === b
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      return a === b
    }
  }

  // methods
  function openSelectPanel() {
    if (disabled || open || options.length === 0) return
    open = true
    if (selectedIndex !== null) {
      highlightedIndex = selectedIndex
      highlightOption(highlightedIndex, false)
    }
  }

  function closeSelectPanel() {
    if (disabled || !open) return
    open = false
    highlightedIndex = null
    keyboardHighlightedIndex = null
    // For multiselect, value is only committed when the panel closes, allowing
    // the user to make multiple selections before the value prop updates.
    if (Array.isArray(selected)) {
      value = (selected.length > 0
        ? selected.map((s) => s.value)
        : null) as unknown as TValue
      onchange?.({ value })
    } else if (selected) {
      // Shallow-copy to create a new reference so that the trigger re-renders.
      // This handles consumers that mutate the selected option's value in-place
      // (e.g. toggling position type in the strategy dropdown).
      selected = { ...selected }
    }
  }

  function registerOption(option: Option) {
    if (options.length === 0) {
      options = [option]
      return
    }
    let highlightedOption =
      highlightedIndex !== null ? options[highlightedIndex] : null

    // Build an ordering map from the DOM to ensure the options array matches
    // the visual order of rendered <li> elements, which may differ from the
    // order onMount callbacks fire.
    let orderMap = Array.from(
      panelRef?.querySelectorAll(`[id^=${id}-option]`) ?? []
    ).reduce(
      (lookup, element, index) =>
        Object.assign(lookup, { [element.id]: index }),
      {}
    ) as Record<string, number>

    // Immutable update — options is $state.raw so we must reassign to trigger reactivity.
    const newOptions = [...options, option]
    newOptions.sort((a, z) => orderMap[a.id] - orderMap[z.id])
    options = newOptions

    highlightedIndex = (() => {
      if (highlightedOption === null) return null
      return options.indexOf(highlightedOption)
    })()
  }

  function unRegisterOption(option: Option) {
    let highlightedOption =
      highlightedIndex !== null ? options[highlightedIndex] : null
    let toRemove = options.findIndex((opt) => opt.value === option.value)
    if (toRemove !== -1) {
      const newOptions = [...options]
      newOptions.splice(toRemove, 1)
      options = newOptions
    }
    highlightedIndex = (() => {
      if (toRemove === highlightedIndex) return null
      if (highlightedOption === null) return null
      return options.indexOf(highlightedOption)
    })()
  }

  function selectOption(index: number | null) {
    if (disabled) return
    if (index !== null && index >= 0 && index < options.length) {
      if (multiselect) {
        // Immutable updates — must create a new array each time so that
        // Svelte detects the change. Mutating in place (push/splice) on the
        // same array reference won't trigger reactivity.
        const current = Array.isArray(selected) ? selected : []
        const indexToDelete = current.findIndex(
          (s) => options[index].id === s.id
        )
        if (indexToDelete > -1) {
          selected = current.filter((_, i) => i !== indexToDelete)
        } else {
          selected = [...current, options[index]]
        }
      } else {
        selectedIndex = index
        selected = options[index] ?? null
        value = (selected?.value ?? null) as unknown as TValue
        onchange?.({ value })
        if (inputRef) {
          inputRef.value = selected?.label ?? ""
        }
      }
    } else {
      value = (inputRef?.value ?? null) as unknown as TValue
    }
  }

  function highlightOption(index: number, keyboard = true) {
    if (disabled) return
    if (keyboard) {
      keyboardHighlightedIndex = index
    } else {
      highlightedIndex = index
    }
  }

  function toggleDialog() {
    if (open) {
      closeSelectPanel()
    } else if (options.length > 0) {
      openSelectPanel()
    }
  }

  function handleKeyDown(event: KeyboardEvent) {
    const shouldContinue = onkeydown?.(event)
    if (shouldContinue === false || !event.key) return
    if (searchDebounce) {
      clearTimeout(searchDebounce)
      searchDebounce = undefined
    }
    if (
      event.key !== Keys.ArrowRight &&
      event.key !== Keys.ArrowLeft &&
      isScrollKey(event.key)
    ) {
      event.preventDefault()
      event.stopPropagation()
    }
    switch (event.key) {
      case Keys.Enter:
        if (!open) {
          openSelectPanel()
          break
        }
        if (
          open &&
          (keyboardHighlightedIndex !== null || highlightedIndex !== null)
        ) {
          event.preventDefault()
          selectOption(
            keyboardHighlightedIndex !== null
              ? keyboardHighlightedIndex
              : highlightedIndex
          )
        }
        if (!multiselect) {
          closeSelectPanel()
        }
        break

      case Keys.Escape:
      case Keys.Tab:
        closeSelectPanel()
        break

      case Keys.Space:
        if (!open) {
          openSelectPanel()
          if (keyboardHighlightedIndex) {
            highlightOption(keyboardHighlightedIndex)
          }
        } else {
          closeSelectPanel()
        }
        break

      case Keys.ArrowDown:
        openSelectPanel()
        highlightOption(nextIndex(options, keyboardHighlightedIndex))
        break

      case Keys.ArrowUp:
        openSelectPanel()
        highlightOption(prevIndex(options, keyboardHighlightedIndex))
        break

      case Keys.Home:
        highlightOption(0)
        break

      case Keys.End:
        highlightOption(options.length - 1)
        break

      case Keys.PageUp:
        highlightOption(prevPage(keyboardHighlightedIndex))
        break

      case Keys.PageDown:
        highlightOption(nextPage(options, keyboardHighlightedIndex))
        break

      default:
        if (event.key.length === 1) {
          handleAlphaNumeric(event)
        }
        break
    }
  }

  // Context uses a getter-based object instead of a writable store. Each getter
  // reads a local $state variable, giving Svelte 5 fine-grained dependency
  // tracking — child components only re-render when the specific properties
  // they read change, not when ANY state changes. This replaces the old pattern
  // of a Writable<SelectState> store + a catch-all $effect that synced all
  // state on every change.
  setContext({
    get rootRef() {
      return rootRef
    },
    set rootRef(v) {
      rootRef = v
    },
    get triggerRef() {
      return triggerRef
    },
    set triggerRef(v) {
      triggerRef = v
    },
    get panelRef() {
      return panelRef
    },
    set panelRef(v) {
      panelRef = v
    },
    get inputRef() {
      return inputRef
    },
    set inputRef(v) {
      inputRef = v
    },
    get open() {
      return open
    },
    get value() {
      return value
    },
    get selectedIndex() {
      return selectedIndex
    },
    get highlightedIndex() {
      return highlightedIndex
    },
    get keyboardHighlightedIndex() {
      return keyboardHighlightedIndex
    },
    get options() {
      return options
    },
    get idPrefix() {
      return id
    },
    get selected() {
      return selected
    },
    get placeholder() {
      return placeholder
    },
    get disabled() {
      return disabled
    },
    get displayDisabledIcon() {
      return !!displayDisabledIcon
    },
    get size() {
      return size
    },
    get multiselect() {
      return multiselect
    },
    openSelectPanel,
    closeSelectPanel,
    toggleDialog,
    handleKeyDown,
    registerOption,
    unRegisterOption,
    select: selectOption,
    highlight: highlightOption,
  })

  // helpers
  function nextIndex(options: Option[], index: number | null) {
    if (index === null) {
      return 0
    }
    return (index + 1) % options.length
  }

  function prevIndex(options: Option[], index: number | null) {
    if (index === null) {
      return options.length - 1
    }
    return index === 0 ? options.length - 1 : index - 1
  }

  function nextPage(options: Option[], index: number | null, pageSize = 10) {
    if (index === null) {
      return 0
    }
    if (index + pageSize >= options.length) {
      return options.length - 1
    }

    return index + pageSize
  }

  function prevPage(index: number | null, pageSize = 10) {
    if (index === null || index - pageSize <= 0) {
      return 0
    }

    return index - pageSize
  }

  let searchStr = ""
  let searchDebounce: ReturnType<typeof setTimeout> | undefined

  async function handleAlphaNumeric(event: KeyboardEvent) {
    if (inputRef) return
    searchDebounce = setTimeout(() => {
      searchStr = ""
    }, 500)
    searchStr += event.key.toLowerCase()
    await tick()
    const index = options.findIndex((o) =>
      o.label?.toLowerCase().startsWith(searchStr)
    )
    if (index >= 0) {
      highlightOption(index)
    }
  }

  onMount(() => {
    if (Array.isArray(value)) {
      selected = options.filter((o) =>
        (value as Array<unknown>).some((v) => valuesEqual(v, o.value))
      )
    } else if (value) {
      const index = options.findIndex((o) => valuesEqual(o.value, value))
      if (index >= 0) {
        selectedIndex = index
        selected = options[index] ?? null
      }
    } else if (selectedIndex !== null) {
      selected = options[selectedIndex] ?? null
      value = (selected?.value ?? null) as unknown as TValue
    }
  })

  // Sync selected state when value changes externally (e.g. parent component
  // updates the value prop). For multiselect, this is gated on !open because
  // value is only committed on close — without this guard, the effect would
  // immediately undo the user's in-progress selection changes.
  $effect(() => {
    const sel = untrack(() => selected)
    if (
      Array.isArray(value) &&
      Array.isArray(sel) &&
      value.length !== sel.length &&
      !open
    ) {
      selected = options.filter((o) =>
        (value as Array<unknown>).some((v) => valuesEqual(v, o.value))
      )
    } else if (!Array.isArray(sel) && !valuesEqual(value, sel?.value)) {
      const optionIndex = options.findIndex((o) => valuesEqual(o.value, value))
      if (optionIndex >= 0) {
        selectedIndex = optionIndex
        selected = options[selectedIndex]
      } else {
        selectedIndex = null
        selected = null
      }
    }
  })

  function handleDocumentClick(event: MouseEvent) {
    if (keepOpen) {
      return
    }
    const target = event.target as Node
    if (rootRef && !rootRef.contains(target) && !panelRef?.contains(target)) {
      closeSelectPanel()
    }
  }
</script>

<svelte:document onpointerdown={handleDocumentClick} />

<Popper.Root>
  <div
    {...restProps}
    class={cn(selectFieldVariants({ error }), className)}
    {id}
    bind:this={rootRef}
    data-open={open}
    data-disabled={disabled}
    data-selected={selected}
    aria-required={required}
    use:useActions={use}
    role="combobox"
    aria-expanded={open}
    aria-controls="{id}-panel"
    aria-activedescendant={selected
      ? Array.isArray(selected)
        ? selected.map((s) => s.id).join(", ")
        : selected.id
      : null}
    tabindex="-1"
    aria-invalid={error}
    {onfocusin}
    {onfocusout}
  >
    {#if trigger}
      {@render trigger()}
    {:else}
      <Trigger />
    {/if}

    {#if panel}
      {@render panel()}
    {:else}
      <SelectPortal>
        <Panel>
          {@render children?.()}
        </Panel>
      </SelectPortal>
    {/if}
    {#if name}
      <input type="hidden" {name} {value} {required} />
    {/if}
  </div>
</Popper.Root>
